import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import IntakeForm from './pages/IntakeForm';
import VideoTraining from './pages/VideoTraining';
import Quiz from './pages/Quiz';
import Acknowledgment from './pages/Acknowledgment';
import Completion from './pages/Completion';
import Admin from './pages/Admin';
import Header from './components/Header';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';
import './App.css';

axios.defaults.withCredentials = true;

function mutationConfig(extra = {}) {
  return {
    ...extra,
    headers: {
      'x-training-request': '1',
      ...(extra.headers || {}),
    },
  };
}

export function normalizeSession(data) {
  if (!data?.authenticated) return { authenticated: false, stage: 'access' };
  const source = data.session || data;
  const sourceEmployee = source.employee || source.profile || source.userData || {};
  const employee = {
    ...sourceEmployee,
    fullName: sourceEmployee.fullName || sourceEmployee.full_name,
    email: sourceEmployee.email,
    role: sourceEmployee.role || sourceEmployee.trainingRole || sourceEmployee.hipaa_training_role,
    officeLocation: sourceEmployee.officeLocation || sourceEmployee.office_location || sourceEmployee.office,
  };
  const serverStage = source.stage || 'training';
  const stage = {
    training: 'training',
    quiz: 'quiz',
    training_complete: 'quiz',
    quiz_started: 'quiz',
    quiz_passed: 'acknowledgment',
    acknowledgement: 'acknowledgment',
    acknowledgment: 'acknowledgment',
    completed: 'completion',
    completion: 'completion',
    revoked: 'access',
  }[serverStage] || 'training';
  if (stage === 'access') return { authenticated: false, stage: 'access' };
  return {
    ...source,
    authenticated: true,
    employee,
    serverStage,
    stage,
  };
}

function App() {
  const [session, setSession] = useState(null);
  const [startupError, setStartupError] = useState('');
  const initializedRef = useRef(false);

  const refreshSession = useCallback(async () => {
    const { data } = await axios.get('/api/session');
    const normalized = normalizeSession(data);
    setSession(normalized);
    return normalized;
  }, []);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    const initialize = async () => {
      const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const activationToken = fragment.get('token');
      try {
        if (activationToken) {
          // Establish a short-lived HttpOnly browser handoff before the
          // one-time HR token is redeemed. The fragment is removed only after
          // the session cookie arrives, so a lost response can be recovered by
          // refreshing this same browser without making the email link reusable
          // from another device.
          await axios.post('/api/access/prepare', {}, mutationConfig());
          await axios.post('/api/access/redeem', { token: activationToken }, mutationConfig());
          window.history.replaceState({}, document.title, '/');
        }
        await refreshSession();
      } catch (error) {
        if (!activationToken && error.response?.status === 401) {
          setSession({ authenticated: false, stage: 'access' });
          return;
        }
        if (activationToken) {
          const status = Number(error.response?.status);
          if (Number.isFinite(status) && status < 500) {
            window.history.replaceState({}, document.title, '/');
          }
          setStartupError(
            error.response?.data?.error ||
            'This training link is invalid, expired, or has already been used. Request a new link below.'
          );
        }
        try {
          await refreshSession();
        } catch (sessionError) {
          setSession({ authenticated: false, stage: 'access' });
        }
      }
    };
    initialize();
  }, [refreshSession]);

  const handleLogout = async () => {
    try {
      await axios.post('/api/access/logout', {}, mutationConfig());
    } catch (_) {}
    localStorage.removeItem('hipaa_training_session');
    localStorage.removeItem('hipaa_video_progress');
    setSession({ authenticated: false, stage: 'access' });
    window.history.replaceState({}, document.title, '/');
  };

  if (!session) {
    return (
      <div className="app-wrapper">
        <Header />
        <main className="main-content"><div className="page-container"><LoadingScreen /></div></main>
        <Footer />
      </div>
    );
  }

  const userData = session.employee;
  const stagePath = {
    training: '/training',
    quiz: '/quiz',
    acknowledgment: '/acknowledgment',
    acknowledgement: '/acknowledgment',
    completion: '/completion',
    completed: '/completion',
  }[session.stage] || '/training';

  const requireSession = (element, allowedStages) => {
    if (!session.authenticated || !userData) return <Navigate to="/" replace />;
    if (allowedStages && !allowedStages.includes(session.stage)) {
      return <Navigate to={stagePath} replace />;
    }
    return element;
  };

  return (
    <Router>
      <div className="app-wrapper">
        <Header />
        <main className="main-content">
          <Routes>
            <Route
              path="/"
              element={session.authenticated ? (
                <Navigate to={stagePath} replace />
              ) : (
                <IntakeForm startupError={startupError} />
              )}
            />
            <Route
              path="/activate"
              element={session.authenticated ? <Navigate to={stagePath} replace /> : <IntakeForm startupError={startupError} />}
            />
            <Route
              path="/training"
              element={requireSession(
                <VideoTraining userData={userData} onComplete={refreshSession} />,
                ['training']
              )}
            />
            <Route
              path="/quiz"
              element={requireSession(
                <Quiz userData={userData} onComplete={refreshSession} />,
                ['quiz']
              )}
            />
            <Route
              path="/acknowledgment"
              element={requireSession(
                <Acknowledgment userData={userData} onComplete={refreshSession} />,
                ['acknowledgment', 'acknowledgement']
              )}
            />
            <Route
              path="/completion"
              element={requireSession(
                <Completion
                  userData={userData}
                  completionData={session.completion}
                  onReset={handleLogout}
                />,
                ['completion', 'completed']
              )}
            />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export { mutationConfig };
export default App;
